import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useFormContext } from 'react-hook-form';
import { FiInfo } from 'react-icons/fi';
import { IconType } from 'react-icons/lib';

import {
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Tooltip,
} from '@brand/components';
import {
  ICheckboxFieldSchema,
  ICheckboxFieldStyles,
  IFieldProps,
} from 'schema/generator/FormSchema';

import { useErrorMessage } from 'hooks/form/useErrorMessage';
import { useStyles } from 'hooks/form/useStyles';

import ComponentIsVisible from 'components/utils/IsVisible';

export const checkboxFieldStyles: ICheckboxFieldStyles = {
  checkboxGroup: {
    spacing: 4,
  },
};

const CheckboxField = ({
  field,
  id,
  name,
}: IFieldProps<ICheckboxFieldSchema>): JSX.Element => {
  const { label, isRequired, shouldDisplay, styles = {} } = field;

  const { t } = useTranslation('form');
  const { register, watch } = useFormContext();
  const fieldStyles = useStyles<ICheckboxFieldStyles>('checkboxField', styles);
  const errorMessage = useErrorMessage(name, label);

  const values = watch(name);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  return (
    <ComponentIsVisible when={isVisible}>
      <FormControl
        isInvalid={!!errorMessage}
        isRequired={isRequired}
        key={`${name}-control`}
        {...fieldStyles.control}
      >
        <ComponentIsVisible when={!!label}>
          <FormLabel htmlFor={name} variant="proRikApp" {...fieldStyles.label}>
            {label}
          </FormLabel>
        </ComponentIsVisible>
        <Flex
          flexDirection={['column']}
          // flexWrap="wrap"
          gap={[2, null, 4]}
          {...fieldStyles.checkboxGroup}
        >
          {field.checkboxes.map(checkbox => (
            <Checkbox
              checked={checkbox.isChecked}
              data-testid={`${id}-${checkbox.name}`}
              defaultChecked={checkbox.isChecked}
              key={checkbox.name}
              {...register(
                field.checkboxes.length === 1
                  ? `${checkbox.name}`
                  : `${name}.${checkbox.name}`,
              )}
            >
              <Flex alignItems="center" color={checkbox.color} gap={2}>
                {checkbox.label || checkbox.name}
                <ComponentIsVisible when={!!checkbox.icon}>
                  <Icon as={checkbox.icon as IconType} h={5} width={5} />
                </ComponentIsVisible>
                <ComponentIsVisible when={!!checkbox.tooltip}>
                  <Tooltip
                    label={checkbox.tooltip}
                    openDelay={200}
                    placement="right"
                  >
                    <span>
                      <FiInfo size={16} />
                    </span>
                  </Tooltip>
                </ComponentIsVisible>
              </Flex>
            </Checkbox>
          ))}
        </Flex>
        <ComponentIsVisible when={!!errorMessage}>
          <FormErrorMessage {...fieldStyles.errorMessage}>
            {t(`validations.${errorMessage}`)}
          </FormErrorMessage>
        </ComponentIsVisible>
      </FormControl>
    </ComponentIsVisible>
  );
};

export default CheckboxField;
